import { useState } from "react"

import { Box, Drawer, InputBase, Stack, Typography } from "@mui/material"
import { alpha } from "@mui/material/styles"

import type { LinkType } from "../../graphql/types"
import type { FC } from ".."
import { IconButton, Link, SvgIcon } from ".."
import useSearchForm from "../../local/hooks/useSearchForm"

type SearchModalProps = {
  open: boolean
  onClose: () => void
  items?: LinkType[]
}

const defaultItems = [
  {
    title: "Specials",
    uri: "/trips",
  },
  {
    title: "Trending Trips",
    uri: "/trips",
  },
  {
    title: "Trip Experiences",
    uri: "/experiences",
  },
  {
    title: "Globetrotting Blog",
    uri: "/inspiration",
  },
]

const SearchModal: FC<SearchModalProps> = ({ open, onClose, items = defaultItems }) => {
  return (
    <Drawer
      open={open}
      onClose={onClose}
      anchor="right"
      slotProps={{
        backdrop: {
          sx: {
            bgcolor: (theme) => alpha(theme.palette["neutral.900"].main, 0.4),
          },
        },
      }}
      SlideProps={{
        timeout: 500,
      }}
      PaperProps={{
        elevation: 0,
        sx: {
          top: 8,
          bottom: 8,
          right: 8,
          height: "auto",
          borderRadius: 1.5,
          width: "min(100vw - 20px, 500px)",
          zIndex: 4100,
        },
      }}
      sx={{
        zIndex: 4000,
      }}
    >
      <Stack alignItems="flex-start" px={12} pt={40} pb={27.5} rowGap={25} position="relative">
        <IconButton
          onClick={onClose}
          variant="base"
          icon={<SvgIcon variant="x" size={16} />}
          sx={(theme) => ({
            border: `1px solid ${alpha(theme.palette["secondary.800"].main, 0.1)}`,
            position: "absolute",
            top: 10,
            right: 10,
            width: 52,
            height: 52,
          })}
        />
        <SearchInput />
        <Stack
          width="100%"
          alignItems="stretch"
          sx={(theme) => ({
            "& > * + *": {
              borderTop: `1px solid ${alpha(theme.palette["neutral.900"].main, 0.1)}`,
            },
          })}
        >
          <Stack justifyContent="center" minHeight={64} sx={{ px: 3, opacity: 0.7 }}>
            <Typography variant="body.md" sx={{ fontSize: { xs: 12 } }}>
              {"Find what you're looking for"}
            </Typography>
          </Stack>
          {items.map((item) => (
            <SeachListItem key={item.uri} {...item} />
          ))}
        </Stack>
      </Stack>
    </Drawer>
  )
}
export default SearchModal

const SearchInput: FC = () => {
  const { submitSearchForm } = useSearchForm()
  const locationSearch = typeof window !== "undefined" ? window.location.search : ""
  const urlParams = new URLSearchParams(locationSearch)
  const [formValue, setFormValue] = useState(
    urlParams.get("keyword") ? urlParams.get("keyword") : ""
  )

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      submitSearchForm(formValue, true)
    }
  }

  return (
    <Box width="100%" position="relative" sx={{ isolation: "isolate" }}>
      <InputBase
        type="text"
        placeholder="What are you looking for?"
        aria-label="What are you looking for?"
        sx={(theme) => ({
          "bgcolor": theme.palette["neutral.100"].main,
          "border": `1px solid ${alpha(theme.palette["neutral.900"].main, 0.1)}`,
          "boxShadow": `1px 1px 2px  ${alpha(theme.palette["neutral.900"].main, 0.1)}`,
          "borderRadius": 1.5,
          "display": "block",
          "fontSize": { xs: 13 },
          "minHeight": 68,
          "p": 5.5,
          "width": "100%",

          "&::placeholder": {
            color: alpha(theme.palette["neutral.700"].main, 0.7),
          },

          "&::-webkit-search-cancel-button": {
            appearance: "none",
            webkitAppearance: "none",
            height: "1em",
            width: "1em",
            borderRadius: "50em",
            background:
              'url("https://pro.fontawesome.com/releases/v5.10.0/svgs/solid/times-circle.svg") no-repeat 50% 50%',
            backgroundSize: "contain",
            opacity: 0,
            pointerEvents: "none",
          },
        })}
        onKeyDown={handleKeyDown}
        defaultValue={formValue}
        onChange={(event) => {
          setFormValue(event.target.value)
        }}
      />
      <SvgIcon
        variant="search"
        size={22}
        sx={{
          fill: (theme) => theme.palette["neutral.700"].main,
          position: "absolute",
          top: "50%",
          right: 22,
          transform: "translateY(-50%)",
          cursor: "pointer",
        }}
        onClick={() => {
          submitSearchForm(formValue, true)
        }}
      />
    </Box>
  )
}

const SeachListItem: FC<LinkType> = ({ uri, title }) => {
  return (
    <Stack justifyContent="center" minHeight={64} px={3}>
      <Link type="base" variant="link.md" href={uri} sx={{ fontSize: { xs: 12 } }}>
        {title}
      </Link>
    </Stack>
  )
}
